import { useMemo } from 'react';
import { APP_FONTS, APP_THEMES, APP_VISUAL_STYLES } from '../constants';
import { dayLabel, fontDescription, onboardingSteps, rangeLabel, rescueKindLabel, themeDescription, visualStyleDescription, t } from '../i18n';
import type { AppFont, AppLanguage, AppTheme, AppVisualStyle, DateRange, FrictionSubtype, InterruptionSource, PingType, RescueKind } from '../types';
import { frictionSubtypeI18nLabel, frictionTypeLabel, interruptionLabel as interruptionSourceLabel } from '../i18n';

type UseAppTextParams = {
  language: AppLanguage;
  theme: AppTheme;
  visualStyle: AppVisualStyle;
  font: AppFont;
  onboardingStep: number;
};

export function useAppText({ language, theme, visualStyle, font, onboardingStep }: UseAppTextParams) {
  const tr = (key: string, vars?: Record<string, string | number>): string => t(language, key, vars);
  const interruptionLabel = (source: InterruptionSource): string => interruptionSourceLabel(language, source);
  const subtypeLabel = (subtype: FrictionSubtype): string => frictionSubtypeI18nLabel(language, subtype);
  const pingTypeLabel = (type: PingType): string => frictionTypeLabel(language, type);
  const rescueKindText = (kind: RescueKind): string => rescueKindLabel(language, kind);
  const activeTheme = APP_THEMES.find((item) => item.theme === theme) ?? APP_THEMES[0];
  const activeThemeDescription = themeDescription(language, activeTheme.theme);
  const activeVisualStyle = APP_VISUAL_STYLES.find((item) => item.style === visualStyle) ?? APP_VISUAL_STYLES[0];
  const activeVisualStyleDescription = visualStyleDescription(language, activeVisualStyle.style);
  const activeFont = APP_FONTS.find((item) => item.font === font) ?? APP_FONTS[0];
  const activeFontDescription = fontDescription(language, activeFont.font);
  const steps = onboardingSteps(language);

  const optionsLabels = useMemo(
    () => ({
      theme: tr('options.theme'),
      styleLanguage: tr('options.style_language'),
      visualTheme: tr('options.visual_theme'),
      typography: tr('options.typography'),
      appFont: tr('options.app_font'),
      language: tr('options.language'),
      appearance: tr('options.appearance'),
      compact: tr('options.compact'),
      simplifiedMode: tr('options.simplified_mode'),
      tips: tr('options.tips'),
      rescueDefaults: tr('options.rescue_defaults'),
      soundDefault: tr('options.sound_default'),
      notifications: tr('options.notifications'),
      notificationsDesc: tr('options.notifications_desc'),
      notificationStyle: tr('options.notification_style'),
      notificationStyleOff: tr('options.notification_style_off'),
      notificationStyleQuiet: tr('options.notification_style_quiet'),
      notificationStyleStandard: tr('options.notification_style_standard'),
      notificationExactAlarm: tr('options.notification_exact_alarm'),
      notificationExactAlarmGranted: tr('options.notification_exact_alarm_granted'),
      notificationExactAlarmDenied: tr('options.notification_exact_alarm_denied'),
      notificationExactAlarmUnknown: tr('options.notification_exact_alarm_unknown'),
      notificationExactAlarmOpenSettings: tr('options.notification_exact_alarm_open_settings'),
      notificationAndroidBatteryNote: tr('options.notification_android_battery_note'),
      notificationAndroidBatteryWarning: tr('options.notification_android_battery_warning'),
      reentryNudges: tr('options.reentry_nudges'),
      dailyReflectionPrompt: tr('options.daily_reflection_prompt'),
      weeklyPatternPrompt: tr('options.weekly_pattern_prompt'),
      safety: tr('options.safety'),
      confirmDelete: tr('options.confirm_delete'),
      monetization: tr('options.monetization'),
      adsEnabled: tr('options.ads_enabled'),
      personalizedAds: tr('options.personalized_ads'),
      personalizedAdsStatus: tr('options.ad_consent_status'),
      personalizedAdsStatusUnknown: tr('options.ad_consent_unknown'),
      personalizedAdsStatusAccepted: tr('options.ad_consent_accepted'),
      personalizedAdsStatusRejected: tr('options.ad_consent_rejected'),
      personalizedAdsStatusNotRequired: tr('options.ad_consent_not_required'),
      withdrawAdConsent: tr('options.withdraw_ad_consent'),
      adPrivacyOptions: tr('options.ad_privacy_options'),
      adsCmpNotice: tr('options.ads_cmp_notice'),
      billingDescription: tr('options.billing_desc'),
      billingConfigured: tr('options.billing_configured'),
      billingNotConfigured: tr('options.billing_not_configured'),
      billingStatus: tr('options.billing_status'),
      billingTier: tr('options.billing_tier'),
      billingTierFree: tr('options.billing_tier_free'),
      billingTierAdFree: tr('options.billing_tier_ad_free'),
      billingTierPro: tr('options.billing_tier_pro'),
      billingProBenefitsTitle: tr('options.billing_pro_benefits_title'),
      billingProBenefitsBody: tr('options.billing_pro_benefits_body'),
      billingCheck: tr('options.billing_check'),
      billingRestore: tr('options.billing_restore'),
      billingDisclosure: tr('options.billing_disclosure'),
      billingPlans: tr('options.billing_plans'),
      billingNoPlans: tr('options.billing_no_plans'),
      billingBuy: tr('options.billing_buy'),
      billingCurrent: tr('options.billing_current'),
      billingManage: tr('options.billing_manage'),
      billingManageUnavailable: tr('options.billing_manage_unavailable'),
      billingDiagnosticsTitle: tr('options.billing_diagnostics_title'),
      billingDiagnosticsRun: tr('options.billing_diagnostics_run'),
      billingDiagnosticsClearEvents: tr('options.billing_diagnostics_clear_events'),
      billingDiagnosticsDone: tr('options.billing_diagnostics_done'),
      billingDiagnosticsFailed: tr('options.billing_diagnostics_failed'),
      billingDiagnosticsNotAvailable: tr('options.billing_diagnostics_not_available'),
      billingDiagnosticsLastCheck: tr('options.billing_diagnostics_last_check'),
      billingDiagnosticsChecks: tr('options.billing_diagnostics_checks'),
      billingDiagnosticsCheckOk: tr('options.billing_diagnostics_check_ok'),
      billingDiagnosticsCheckNeedsAction: tr('options.billing_diagnostics_check_needs_action'),
      billingDiagnosticsDetails: tr('options.billing_diagnostics_details'),
      billingDiagnosticsAppUserId: tr('options.billing_diagnostics_app_user_id'),
      billingDiagnosticsOffering: tr('options.billing_diagnostics_offering'),
      billingDiagnosticsPackages: tr('options.billing_diagnostics_packages'),
      billingDiagnosticsEntitlements: tr('options.billing_diagnostics_entitlements'),
      billingDiagnosticsSubscriptions: tr('options.billing_diagnostics_subscriptions'),
      billingDiagnosticsManagementUrl: tr('options.billing_diagnostics_management_url'),
      billingDiagnosticsRecentEvents: tr('options.billing_diagnostics_recent_events'),
      billingDiagnosticsNoEvents: tr('options.billing_diagnostics_no_events'),
      billingAdFreeTag: tr('options.billing_tier_ad_free'),
      billingProTag: tr('options.billing_tier_pro'),
      backup: tr('options.backup'),
      backupDescription: tr('options.backup_desc'),
      localFirstTitle: tr('options.local_first_title'),
      localFirstBody: tr('options.local_first_body'),
      localFirstRisk: tr('options.local_first_risk'),
      backupPassphrase: tr('options.backup_passphrase'),
      backupCreate: tr('options.backup_create'),
      backupRestore: tr('options.backup_restore'),
      backupStatusPrefix: tr('options.backup_status'),
      syncInProgress: tr('options.sync_in_progress'),
      drive: tr('options.drive'),
      driveDescription: tr('options.drive_desc'),
      driveConnect: tr('options.drive_connect'),
      driveDisconnect: tr('options.drive_disconnect'),
      driveUpload: tr('options.drive_upload'),
      driveRestore: tr('options.drive_restore'),
      driveConfigured: tr('options.drive_configured'),
      driveNotConfigured: tr('options.drive_not_configured'),
      driveConnected: tr('options.drive_connected'),
      driveNotConnected: tr('options.drive_not_connected'),
      help: tr('options.help'),
      replayTitle: tr('options.replay_title'),
      replayDescription: tr('options.replay_desc'),
      replayButton: tr('options.replay_btn'),
      privacyData: tr('options.privacy_data'),
      privacyDescription: tr('options.privacy_desc'),
      privacyPolicy: tr('options.privacy_policy'),
      terms: tr('options.terms'),
      privacyRightsSla: tr('options.privacy_rights_sla'),
      childrenNotice: tr('options.children_notice'),
      medicalDisclaimer: tr('legal.disclaimer'),
      contactSupport: tr('options.contact_support'),
      exportData: tr('options.privacy_export_data'),
      deleteData: tr('options.privacy_delete_data')
    }),
    [language]
  );

  const insightsLabels = useMemo(
    () => ({
      todaySnapshot: tr('insights.today_snapshot'),
      pings: tr('insights.pings'),
      recovered: tr('insights.recovered'),
      avgStuck: tr('insights.avg_stuck'),
      patterns: tr('insights.patterns'),
      calendarView: tr('insights.calendar_view'),
      swipeHint: tr('insights.swipe_hint'),
      topTypes: tr('insights.top_types'),
      topTypesForBucket: tr('insights.top_types'),
      none: tr('common.none'),
      sequences: tr('insights.sequences'),
      medianGap: tr('insights.median_gap'),
      whatHelps: tr('insights.what_helps'),
      feltHelpful: tr('insights.felt_helpful'),
      outcomesLogged: tr('insights.outcomes_logged'),
      smallSample: tr('insights.small_sample'),
      started: tr('insights.started'),
      completion: tr('insights.completion'),
      resolved: tr('insights.resolved'),
      medianToNextPing: tr('insights.median_next_ping'),
      associatedHint: tr('insights.associated_hint'),
      interruptionSources: tr('insights.interruption_sources'),
      noInterruptions: tr('insights.no_interruptions'),
      frictionSubtypes: tr('insights.friction_subtypes'),
      noSubtypes: tr('insights.no_subtypes'),
      tinyWins: tr('insights.tiny_wins'),
      captured: tr('insights.captured'),
      completedRescues: tr('insights.completed_rescues'),
      captureRate: tr('insights.capture_rate'),
      latest: tr('insights.latest'),
      noneYet: tr('insights.none_yet'),
      prepChecklist: tr('insights.prep_checklist'),
      timedRescues: tr('insights.timed_rescues'),
      phoneSilenced: tr('insights.phone_silenced'),
      tabsClosed: tr('insights.tabs_closed'),
      docOpen: tr('insights.doc_open'),
      timerVisible: tr('insights.timer_visible'),
      frictionStart: tr('insights.friction_start'),
      pingsWithSlippingTimer: tr('insights.pings_slipping_timer'),
      medianStuckBeforePing: tr('insights.median_stuck_before_ping'),
      meanStuckBeforePing: tr('insights.mean_stuck_before_ping'),
      parkingLot: tr('insights.parking_lot'),
      noParkedThoughts: tr('insights.no_parked'),
      export: tr('insights.export'),
      exportJson: tr('insights.export_json'),
      exportCsv: tr('insights.export_csv'),
      empty: tr('insights.empty'),
      expandAll: tr('insights.expand_all'),
      collapseAll: tr('insights.collapse_all'),
      dayBreakdown: tr('insights.day_breakdown'),
      noDayData: tr('insights.no_day_data'),
      selectedDay: tr('insights.selected_day'),
      rescuesStarted: tr('insights.rescues_started'),
      topRescues: tr('insights.top_rescues'),
      hourPattern: tr('insights.hour_pattern'),
      prevMonth: tr('insights.prev_month'),
      nextMonth: tr('insights.next_month'),
      eventDetails: tr('insights.event_details'),
      startsAt: tr('insights.starts_at'),
      endsAt: tr('insights.ends_at'),
      duration: tr('insights.duration'),
      status: tr('insights.status'),
      completed: tr('detail.completed'),
      canceled: tr('detail.canceled'),
      proLockedTitle: tr('insights.pro_locked_title'),
      proLockedBody: tr('insights.pro_locked_body'),
      proUpgradeCta: tr('insights.pro_upgrade_cta')
    }),
    [language]
  );

  const rescuePickerLabels = useMemo(
    () => ({
      whatsHappening: tr('rescuepicker.whats_happening'),
      pulledAway: tr('rescuepicker.pulled_away'),
      none: tr('common.none'),
      chooseRescue: tr('rescuepicker.choose_rescue'),
      contextStep: tr('rescuepicker.context_step'),
      contextHint: tr('rescuepicker.context_hint'),
      rescueStep: tr('rescuepicker.rescue_step'),
      rescueHint: tr('rescuepicker.rescue_hint'),
      supportStep: tr('rescuepicker.support_step'),
      supportHint: tr('rescuepicker.support_hint'),
      topPick: tr('log.quick_top_pick'),
      favorites: tr('rescuepicker.favorites'),
      recentContextHint: tr('rescuepicker.recent_context_hint'),
      applyLastContext: tr('rescuepicker.apply_last_context'),
      start: tr('rescuepicker.start'),
      prepChecklist: tr('rescuepicker.prep_checklist'),
      resetTip: tr('rescuepicker.reset_tip'),
      optionalDetails: tr('rescuepicker.optional_details'),
      smallestStepPlaceholder: tr('rescuepicker.smallest_step'),
      start10mWithNote: tr('rescuepicker.start_10m_note'),
      sentencePlaceholder: tr('rescuepicker.sentence_placeholder'),
      saveSentence: tr('rescuepicker.save_sentence'),
      focusSound: tr('rescuepicker.focus_sound'),
      editRescueText: tr('rescuepicker.edit_text'),
      saveScript: tr('rescuepicker.save_script'),
      savedSuffix: tr('common.saved'),
      favoriteOn: tr('rescuepicker.favorite_on'),
      favoriteOff: tr('rescuepicker.favorite_off')
    }),
    [language]
  );

  const prepItemLabel = (key: 'phoneSilenced' | 'tabsClosed' | 'docOpen' | 'timerVisible'): string => {
    if (key === 'phoneSilenced') return tr('insights.phone_silenced');
    if (key === 'tabsClosed') return tr('insights.tabs_closed');
    if (key === 'docOpen') return tr('insights.doc_open');
    return tr('insights.timer_visible');
  };

  const rescueGuide = (kind: RescueKind) => ({
    summary: tr(`rescueguide.${kind}_summary`),
    prompt:
      kind === 'make_smaller_10m'
        ? tr('rescueguide.make_smaller_prompt')
        : kind === 'brain_dump_2m'
          ? tr('rescueguide.brain_dump_prompt')
          : kind === 'one_sentence'
            ? tr('rescueguide.one_sentence_prompt')
            : null,
    actionLabel:
      kind === 'make_smaller_10m'
        ? tr('rescueguide.make_smaller_action')
        : kind === 'brain_dump_2m'
          ? tr('rescueguide.brain_dump_action')
          : kind === 'one_sentence'
            ? tr('rescueguide.one_sentence_action')
            : tr('rescuepicker.start'),
    breathing: {
      intro: tr('rescueguide.breathe_intro'),
      inhale: tr('rescueguide.breathe_in'),
      hold: tr('rescueguide.breathe_hold'),
      exhale: tr('rescueguide.breathe_out')
    }
  });

  return {
    tr,
    interruptionLabel,
    subtypeLabel,
    pingTypeLabel,
    rescueKindText,
    activeThemeDescription,
    activeVisualStyleDescription,
    activeFontDescription,
    steps,
    optionsLabels,
    insightsLabels,
    rescuePickerLabels,
    bottomNavLabels: {
      log: tr('tab.log'),
      rescue: tr('tab.rescue'),
      history: tr('tab.history'),
      insights: tr('tab.insights'),
      options: tr('tab.options')
    },
    pingRescueSheetLabels: {
      title: tr('rescue.what_helped'),
      close: tr('common.close'),
      saved: tr('common.saved')
    },
    quickLogLabels: {
      title: tr('log.quick_sheet_title'),
      close: tr('common.close'),
      body: tr('log.quick_sheet_body'),
      primaryTitle: tr('log.quick_primary_title'),
      primaryBody: tr('log.quick_primary_body'),
      optional: tr('log.quick_optional'),
      skip: tr('log.quick_sheet_skip'),
      favorites: tr('rescuepicker.favorites'),
      recentContextHint: tr('rescuepicker.recent_context_hint'),
      applyLastContext: tr('rescuepicker.apply_last_context'),
      alternateRescues: tr('log.quick_other_rescues'),
      start: tr('rescuepicker.start')
    },
    pingDetailLabels: {
      title: tr('detail.title'),
      close: tr('common.close'),
      type: tr('detail.type'),
      stuckBeforePing: tr('detail.stuck_before_ping'),
      subtype: tr('detail.subtype'),
      interruptedBy: tr('detail.interrupted_by'),
      completed: tr('detail.completed'),
      canceled: tr('detail.canceled'),
      tinyWin: tr('detail.tiny_win'),
      prep: tr('detail.prep'),
      phone: tr('detail.phone'),
      tabs: tr('detail.tabs'),
      doc: tr('detail.doc'),
      timer: tr('detail.timer'),
      addNote: tr('detail.add_note'),
      saveNote: tr('detail.save_note'),
      deletePing: tr('detail.delete_ping'),
      na: tr('common.na')
    },
    parkingLabels: {
      title: tr('parking.title'),
      close: tr('common.close'),
      description: tr('parking.desc'),
      placeholder: tr('parking.placeholder'),
      cancel: tr('common.cancel'),
      save: tr('parking.save')
    },
    consentLabels: {
      onboarding: {
        welcome: tr('onboarding.welcome'),
        skip: tr('onboarding.skip'),
        step: tr('onboarding.step', { current: onboardingStep + 1, total: steps.length }),
        back: tr('onboarding.back'),
        next: tr('onboarding.next'),
        start: tr('onboarding.start')
      },
      disclosure: {
        title: tr('disclosure.title'),
        body: tr('disclosure.body'),
        localData: tr('disclosure.local_data'),
        cloudBackup: tr('disclosure.cloud_backup'),
        billing: tr('disclosure.billing'),
        personalizedAds: tr('disclosure.personalized_ads'),
        continue: tr('disclosure.continue'),
        exit: tr('disclosure.exit'),
        requiredHint: tr('disclosure.required_hint')
      },
      adConsent: {
        title: tr('adsconsent.title'),
        body: tr('adsconsent.body'),
        allow: tr('adsconsent.allow'),
        keepNonPersonalized: tr('adsconsent.keep_non_personalized')
      },
      legalConsent: {
        title: tr('legal.title'),
        body: tr('legal.body'),
        privacy: tr('options.privacy_policy'),
        terms: tr('options.terms'),
        disclaimer: tr('legal.disclaimer'),
        accept: tr('legal.accept'),
        exit: tr('legal.exit')
      },
      legalDocument: {
        privacyTitle: tr('legal.doc_title_privacy'),
        termsTitle: tr('legal.doc_title_terms'),
        privacyContent: tr('legal.privacy_text'),
        termsContent: tr('legal.terms_text'),
        close: tr('common.close'),
        openExternal: tr('legal.doc_open_external'),
        lastUpdated: tr('legal.doc_last_updated')
      }
    },
    rangeLabel: (range: DateRange) => rangeLabel(language, range),
    dayLabel: (day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun') => dayLabel(language, day),
    prepItemLabel,
    rescueGuide
  };
}
